import { createTheme, Theme } from '@mui/material/styles';

// Define our enhanced color palette for both light and dark modes
export const lightColors = {
  primary: {
    main: '#1A365D',      // Deep blue
    light: '#2C5282',     // Lighter blue
    dark: '#0F2A44',      // Darker blue
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#38B2AC',      // Teal
    light: '#4FD1C7',     // Light teal
    dark: '#319795',      // Dark teal
    contrastText: '#FFFFFF',
  },
  accent: {
    gold: '#D69E2E',      // Gold for premium tiers
    platinum: '#718096',  // Platinum color
    diamond: '#B794F6',   // Diamond/purple
    success: '#48BB78',   // Green for positive values
    warning: '#ED8936',   // Orange for warnings
    error: '#F56565',     // Red for urgent items
  },
  background: {
    default: '#F7FAFC',   // Very light gray
    paper: '#FFFFFF',     // White
    elevated: '#EDF2F7',  // Light gray for elevated surfaces
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  text: {
    primary: '#1A202C',   // Almost black
    secondary: '#4A5568', // Dark gray
    disabled: '#A0AEC0',  // Light gray
  },
  gradients: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gold: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    platinum: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    diamond: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    success: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
  }
};

export const darkColors = {
  primary: {
    main: '#4299E1',      // Brighter blue for dark mode
    light: '#63B3ED',     // Light blue
    dark: '#2B6CB0',      // Dark blue
    contrastText: '#1A202C',
  },
  secondary: {
    main: '#4FD1C7',      // Bright teal
    light: '#81E6D9',     // Light teal
    dark: '#38B2AC',      // Dark teal
    contrastText: '#1A202C',
  },
  accent: {
    gold: '#F6E05E',      // Brighter gold
    platinum: '#A0AEC0',  // Light platinum
    diamond: '#D6BCFA',   // Light purple
    success: '#68D391',   // Bright green
    warning: '#F6AD55',   // Bright orange
    error: '#FC8181',     // Bright red
  },
  background: {
    default: '#0D1117',   // GitHub dark
    paper: '#161B22',     // Slightly lighter
    elevated: '#21262D',  // Card background
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
  },
  text: {
    primary: '#F7FAFC',   // Almost white
    secondary: '#E2E8F0', // Light gray
    disabled: '#718096',  // Medium gray
  },
  gradients: {
    primary: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
    gold: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    platinum: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
    diamond: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
    success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  }
};

// Create theme factory
export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const colors = mode === 'light' ? lightColors : darkColors;
  
  return createTheme({
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: colors.text,
      success: {
        main: colors.accent.success,
      },
      warning: {
        main: colors.accent.warning,
      },
      error: {
        main: colors.accent.error,
      },
      info: {
        main: colors.secondary.main,
      },
    },
    typography: {
      fontFamily: '"Inter", "SF Pro Display", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 16,
    },
    shadows: mode === 'light' ? [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.05)',
      '0px 4px 8px rgba(0, 0, 0, 0.1)',
      '0px 8px 16px rgba(0, 0, 0, 0.1)',
      '0px 16px 32px rgba(0, 0, 0, 0.1)',
      '0px 24px 48px rgba(0, 0, 0, 0.15)',
      ...Array(19).fill('0px 24px 48px rgba(0, 0, 0, 0.15)'),
    ] as any : [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.2)',
      '0px 4px 8px rgba(0, 0, 0, 0.3)',
      '0px 8px 16px rgba(0, 0, 0, 0.3)',
      '0px 16px 32px rgba(0, 0, 0, 0.3)',
      '0px 24px 48px rgba(0, 0, 0, 0.4)',
      ...Array(19).fill('0px 24px 48px rgba(0, 0, 0, 0.4)'),
    ] as any,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            backdropFilter: 'blur(20px)',
            border: mode === 'light' 
              ? '1px solid rgba(255, 255, 255, 0.2)' 
              : '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 600,
            padding: '10px 20px',
            fontSize: '0.95rem',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          contained: {
            boxShadow: mode === 'light' 
              ? '0px 4px 12px rgba(0, 0, 0, 0.15)' 
              : '0px 4px 12px rgba(0, 0, 0, 0.4)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: mode === 'light' 
                ? '0px 8px 25px rgba(0, 0, 0, 0.2)' 
                : '0px 8px 25px rgba(0, 0, 0, 0.5)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 25,
            fontWeight: 600,
            fontSize: '0.8rem',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(20px)',
            backgroundColor: mode === 'light' 
              ? 'rgba(255, 255, 255, 0.8)' 
              : 'rgba(13, 17, 23, 0.8)',
          },
        },
      },
    },
  });
};

// Default themes
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');

// Legacy export for backward compatibility
export const theme = lightTheme;

// Tier colors mapping with theme support
export const getTierColor = (tier: string, mode: 'light' | 'dark' = 'light'): string => {
  const colors = mode === 'light' ? lightColors : darkColors;
  const tierLower = tier.toLowerCase();
  if (tierLower.includes('diamond') || tierLower.includes('black')) return colors.accent.diamond;
  if (tierLower.includes('platinum') || tierLower.includes('explorer')) return colors.accent.platinum;
  if (tierLower.includes('gold') || tierLower.includes('premier')) return colors.accent.gold;
  return colors.secondary.main;
};

// Program type colors with theme support
export const getProgramTypeColor = (type: 'airline' | 'hotel' | 'credit_card', mode: 'light' | 'dark' = 'light'): string => {
  const colors = mode === 'light' ? lightColors : darkColors;
  switch (type) {
    case 'airline': return '#3182CE';
    case 'hotel': return colors.accent.success;
    case 'credit_card': return '#D53F8C';
    default: return colors.primary.main;
  }
};